
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../../hooks/useProducts';

const dayAbbr: Record<string, string> = {
    'Domingo': 'Dom', 'Lunes': 'Lun', 'Martes': 'Mar', 'Miércoles': 'Mié',
    'Jueves': 'Jue', 'Viernes': 'Vie', 'Sábado': 'Sáb',
};

const Footer: React.FC = () => {
    const currentYear = new Date().getFullYear();
    const { helpSettings, brandingSettings } = useProducts();

    const scheduleLines = useMemo(() => {
        const enabled = helpSettings.schedule.filter(d => d.enabled).sort((a, b) => a.dayIndex - b.dayIndex);
        if (enabled.length === 0) return [];
        const groups: { days: string[]; hours: string }[] = [];
        for (const d of enabled) {
            const hours = `${d.start} - ${d.end}`;
            const abbr = dayAbbr[d.day] || d.day;
            const last = groups[groups.length - 1];
            if (last && last.hours === hours) {
                last.days.push(abbr);
            } else {
                groups.push({ days: [abbr], hours });
            }
        }
        return groups.map(g => {
            const label = g.days.length > 2
                ? `${g.days[0]} a ${g.days[g.days.length - 1]}`
                : g.days.join(' y ');
            return `${label}: ${g.hours}`;
        });
    }, [helpSettings.schedule]);

    const formatPhone = (num: string) => {
        const clean = num.replace(/\D/g, '');
        if (clean.startsWith('521') && clean.length === 13) {
            const local = clean.slice(3);
            return `+52 ${local.slice(0, 2)} ${local.slice(2, 6)} ${local.slice(6)}`;
        }
        if (clean.startsWith('52') && clean.length === 12) {
            const local = clean.slice(2);
            return `+52 ${local.slice(0, 2)} ${local.slice(2, 6)} ${local.slice(6)}`;
        }
        return num;
    };

    return (
        <footer className="bg-slate-900 text-slate-300 pt-16 pb-8 border-t border-slate-800">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
                    {/* Brand Column */}
                    <div className="space-y-4">
                        <Link to="/" className="flex items-center space-x-2 mb-4">
                            {brandingSettings.logoUrl ? (
                                <img src={brandingSettings.logoUrl} alt={brandingSettings.platformName} className="h-8 w-8 object-contain" />
                            ) : (
                                <i className={`${brandingSettings.logoIcon} text-3xl text-brand-secondary`}></i>
                            )}
                            <span className="text-2xl font-bold text-white">
                                {brandingSettings.platformNameAccent && brandingSettings.platformName.includes(brandingSettings.platformNameAccent)
                                    ? <>{brandingSettings.platformName.split(brandingSettings.platformNameAccent)[0]}<span className="text-brand-secondary">{brandingSettings.platformNameAccent}</span>{brandingSettings.platformName.split(brandingSettings.platformNameAccent).slice(1).join(brandingSettings.platformNameAccent)}</>
                                    : brandingSettings.platformName
                                }
                            </span>
                        </Link>
                        <p className="text-sm leading-relaxed">
                            La imprenta digital más rápida de México. Combinamos tecnología de punta con un servicio obsesionado con la velocidad y la calidad.
                        </p>
                        <div className="flex space-x-4 pt-2">
                            <a href="https://www.facebook.com/impresionveloz/" target="_blank" rel="noopener noreferrer" aria-label="Visítanos en Facebook" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all">
                                <i className="fab fa-facebook-f"></i>
                            </a>
                            <a href="https://www.instagram.com/impresionveloz" target="_blank" rel="noopener noreferrer" aria-label="Síguenos en Instagram" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-pink-600 hover:text-white transition-all">
                                <i className="fab fa-instagram"></i>
                            </a>
                            <a href="https://www.tiktok.com/@impresion.veloz" target="_blank" rel="noopener noreferrer" aria-label="Síguenos en TikTok" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-black hover:text-white transition-all">
                                <i className="fab fa-tiktok"></i>
                            </a>
                            <a href="https://www.youtube.com/@ImpresionVeloz" target="_blank" rel="noopener noreferrer" aria-label="Síguenos en YouTube" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-red-600 hover:text-white transition-all">
                                <i className="fab fa-youtube"></i>
                            </a>
                        </div>
                    </div>

                    {/* Products Links */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-6">Productos Populares</h3>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/oficina/tarjetas-de-presentacion-clasicas" className="hover:text-blue-400 transition-colors">Tarjetas de Presentación</Link></li>
                            <li><Link to="/publicidad/volantes-publicitarios" className="hover:text-blue-400 transition-colors">Volantes y Flyers</Link></li>
                            <li><Link to="/gran-formato/lonas-impresas" className="hover:text-blue-400 transition-colors">Lonas Publicitarias</Link></li>
                            <li><Link to="/promocionales/stickers-personalizados" className="hover:text-blue-400 transition-colors">Stickers y Etiquetas</Link></li>
                            <li><Link to="/" className="hover:text-blue-400 transition-colors">Ver Catálogo Completo</Link></li>
                        </ul>
                    </div>

                    {/* Support Links */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-6">Ayuda y Soporte</h3>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/track" className="hover:text-blue-400 transition-colors">Rastrear Pedido</Link></li>
                            <li><a href="#" className="hover:text-blue-400 transition-colors">Preguntas Frecuentes</a></li>
                            <li><a href="#" className="hover:text-blue-400 transition-colors">Guía de Archivos</a></li>
                            <li><a href="#" className="hover:text-blue-400 transition-colors">Facturación</a></li>
                            <li><a href="#" className="hover:text-blue-400 transition-colors">Términos y Condiciones</a></li>
                            <li><Link to="/eliminacion-de-datos" className="hover:text-blue-400 transition-colors">Eliminación de Datos</Link></li>
                            <li><Link to="/politica-de-devoluciones" className="hover:text-blue-400 transition-colors">Política de Devoluciones</Link></li>
                            <li><Link to="/contentengine/terminos-de-servicio" className="hover:text-blue-400 transition-colors">Términos de Servicio</Link></li>
                            <li><Link to="/contentengine/aviso-de-privacidad" className="hover:text-blue-400 transition-colors">Aviso de Privacidad</Link></li>
                        </ul>
                    </div>

                    {/* Contact & Trust */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-6">Contáctanos</h3>
                        <ul className="space-y-4 text-sm">
                            {helpSettings.whatsappNumber && (
                                <li className="flex items-start">
                                    <i className="fab fa-whatsapp text-green-500 mt-1 mr-3 text-lg"></i>
                                    <span>
                                        <span className="block text-xs text-slate-500 uppercase font-bold">Ventas y Cotizaciones</span>
                                        <a href={`https://wa.me/${helpSettings.whatsappNumber}`} className="hover:text-white font-medium">{formatPhone(helpSettings.whatsappNumber)}</a>
                                    </span>
                                </li>
                            )}
                            {helpSettings.contactPhone && (
                                <li className="flex items-start">
                                    <i className="fas fa-phone text-blue-400 mt-1 mr-3"></i>
                                    <span>
                                        <span className="block text-xs text-slate-500 uppercase font-bold">Teléfono</span>
                                        <a href={`tel:${helpSettings.contactPhone}`} className="hover:text-white font-medium">{formatPhone(helpSettings.contactPhone)}</a>
                                    </span>
                                </li>
                            )}
                            {helpSettings.contactEmail && (
                                <li className="flex items-start">
                                    <i className="fas fa-envelope text-blue-500 mt-1 mr-3"></i>
                                    <span>
                                        <span className="block text-xs text-slate-500 uppercase font-bold">Email</span>
                                        <a href={`mailto:${helpSettings.contactEmail}`} className="hover:text-white">{helpSettings.contactEmail}</a>
                                    </span>
                                </li>
                            )}
                            {scheduleLines.length > 0 && (
                                <li className="flex items-start">
                                    <i className="fas fa-clock text-orange-500 mt-1 mr-3"></i>
                                    <span>
                                        <span className="block text-xs text-slate-500 uppercase font-bold">Horario de Atención</span>
                                        {scheduleLines.map((line, i) => (
                                            <React.Fragment key={i}>
                                                {line}{i < scheduleLines.length - 1 ? <br /> : null}
                                            </React.Fragment>
                                        ))}
                                    </span>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>

                <div className="border-t border-slate-800 pt-8 mt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="text-xs text-slate-500 text-center md:text-left">
                        <p>&copy; {currentYear} {brandingSettings.platformName}. Todos los derechos reservados.</p>
                        <p className="mt-1"><i className="fas fa-map-marker-alt mr-1"></i>Cuauhtémoc, CDMX 06800, México</p>
                    </div>
                    
                    {/* Payment Methods */}
                    <div className="flex items-center space-x-3 opacity-70 grayscale hover:grayscale-0 transition-all duration-300">
                        <i className="fab fa-cc-visa text-3xl text-white"></i>
                        <i className="fab fa-cc-mastercard text-3xl text-white"></i>
                        <i className="fab fa-cc-amex text-3xl text-white"></i>
                        <i className="fab fa-cc-paypal text-3xl text-white"></i>
                        <i className="fab fa-cc-stripe text-3xl text-white"></i>
                        <div className="bg-white text-blue-900 font-bold px-1 rounded text-xs h-6 flex items-center">SPEI</div>
                        <div className="bg-[#00bcff] text-white font-bold px-1.5 rounded text-xs h-6 flex items-center">MP</div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
